import React, { useEffect, useRef, useState } from 'react'
import { faceTrackState } from '../lib/faceTrackState.js'
import { resolveFaceTrackIndicator } from '../lib/faceTrackIndicatorState.js'
import { isFaceChipVisible, setFaceChipVisible, subscribeFaceChipVisibility } from '../lib/faceTrackChipVisibility.js'
import { createFaceClipRecorder } from '../lib/faceClipRecorder.js'
import { saveFaceCapture, captureDefaultName } from '../lib/faceCaptureSave.js'
import DirtyBadge from './DirtyBadge'

const TONE_COLOR = {
  neutral: 'var(--m-fg-2)',
  tracking: 'var(--m-online)',
  danger: 'var(--m-danger)',
}

/**
 * FaceTrackIndicator — the Face Tracker viewport pill (ENG-1107). Visible
 * only while a Face Tracker node is PLUGGED into the face graph: 'starting…',
 * the tracking chip with REC, the recording chip with elapsed time, and
 * blocked/error with the reason + Retry (those never auto-retry). After a take
 * is stopped, a small save panel (name, Save, Discard, DirtyBadge) appears.
 *
 * It is parked TOP-RIGHT under the menubar (it used to sit mid-viewport, over
 * the avatar's chest, with no way to move or close it) and carries a × that
 * dismisses it; the View menu's "Face Tracker chip" brings it back. Dismissing
 * is purely visual — the camera and any take in progress carry on. A blocked or
 * failed camera, and an unsaved take, show anyway: each carries the only button
 * that resolves it, so hiding them would just lose the session silently.
 *
 * The recorder is a ref, fed from faceTrackState.onFrame at CAPTURE cadence;
 * React state here changes at most a few times a second (elapsed readout),
 * never per render frame. Sibling of <HeadTrackIndicator/> — same pill
 * treatment, parked one row above it.
 */
export default function FaceTrackIndicator() {
  const [state, setState] = useState(faceTrackState.state)
  const [faceSeen, setFaceSeen] = useState(faceTrackState.faceSeen)
  const [errorMessage, setErrorMessage] = useState(faceTrackState.errorMessage)
  const [recording, setRecording] = useState(false)
  const [, setTick] = useState(0) // elapsed readout refresh (2 Hz while recording)
  const [take, setTake] = useState(null) // finished clip awaiting save/discard
  const [name, setName] = useState('')
  const [saveState, setSaveState] = useState('clean') // clean | saving | error
  const [saveError, setSaveError] = useState(null)
  const [chipVisible, setChipVisible] = useState(isFaceChipVisible)
  const recorder = useRef(null)
  if (!recorder.current) recorder.current = createFaceClipRecorder({ fps: faceTrackState.config.trackFps })

  useEffect(() => subscribeFaceChipVisibility(setChipVisible), [])

  useEffect(() => faceTrackState.onStateChange((next) => {
    setState(next)
    setFaceSeen(faceTrackState.faceSeen)
    setErrorMessage(faceTrackState.errorMessage)
    // Losing the session mid-take keeps what was recorded and offers to save it.
    if (next !== 'tracking' && recorder.current.active) stopRec()
  }), [])

  useEffect(() => {
    if (!recording) return undefined
    const unsub = faceTrackState.onFrame((st, tMs) => {
      const ok = recorder.current.push(st.arkit, tMs, {
        landmarks: st.landmarkCount ? st.landmarks : null,
        matrix: st.hasMatrix ? st.matrix : null,
        gnm: st.gnm ?? null,
      })
      if (!ok && !recorder.current.active) stopRec() // the max-duration cap fired
    })
    const timer = setInterval(() => setTick((n) => n + 1), 500)
    return () => { unsub(); clearInterval(timer) }
  }, [recording])

  function startRec() {
    recorder.current.begin(faceTrackState.frameT || performance.now())
    setSaveState('clean'); setSaveError(null); setTake(null)
    setRecording(true)
  }

  function stopRec() {
    const clip = recorder.current.finish()
    setRecording(false)
    if (clip) { setTake(clip); setName(captureDefaultName()) }
  }

  async function save() {
    setSaveState('saving'); setSaveError(null)
    try {
      await saveFaceCapture({ name, clip: take })
      setSaveState('clean'); setTake(null)
    } catch (err) {
      setSaveState('error'); setSaveError(err?.message || String(err))
    }
  }

  const ui = resolveFaceTrackIndicator({
    state, faceSeen, errorMessage, recording,
    elapsedMs: recorder.current.elapsedMs, frameCount: recorder.current.frameCount,
  })

  const handleAction = (id) => {
    if (id === 'rec') startRec()
    else if (id === 'stoprec') stopRec()
    else if (id === 'retry') faceTrackState.retry()
  }

  const color = TONE_COLOR[ui.tone] || 'var(--m-fg-2)'
  const pillStyle = {
    position: 'absolute', top: 44, right: 12,
    display: 'flex', alignItems: 'center', gap: 8, padding: '6px 14px', borderRadius: 999,
    background: 'color-mix(in srgb, var(--m-panel) 90%, transparent)', border: `1px solid ${color}`,
    color, fontFamily: 'var(--m-font)', fontSize: 11, pointerEvents: 'auto', zIndex: 100000,
  }
  const btn = (extra) => ({
    background: 'transparent', border: `1px solid ${color}`, borderRadius: 999, color,
    fontFamily: 'var(--m-font)', fontSize: 11, lineHeight: 1, padding: '3px 8px', cursor: 'pointer', ...extra,
  })

  // A camera that is blocked or failed keeps the chip on screen whatever the
  // preference says: Retry lives nowhere else on the viewport.
  const forced = state === 'blocked' || state === 'error'
  const showPill = ui.visible && (chipVisible || forced)

  if (!showPill && !take) return null

  return (
    <>
      {showPill && (
        <div data-testid="face-track-indicator" data-state={state} data-recording={recording ? '1' : '0'} style={pillStyle}>
          {ui.dot && <span data-testid="face-track-dot" style={{ width: 8, height: 8, borderRadius: '50%', background: ui.dot === 'solid' ? color : 'transparent', border: `1px solid ${color}` }} />}
          <span>{ui.label}</span>
          {ui.actions.map((a) => (
            <button key={a.id} type="button" data-testid={`face-track-action-${a.id}`} onClick={() => handleAction(a.id)} style={btn()}>
              {a.label}
            </button>
          ))}
          <button type="button" data-testid="face-track-hide" title="Hide this chip — View ▸ Face Tracker chip brings it back"
            onClick={() => setFaceChipVisible(false)} style={btn({ border: 'none', padding: '3px 2px', fontSize: 13 })}>
            ×
          </button>
        </div>
      )}
      {take && (
        <div data-testid="face-track-take" style={{ ...pillStyle, top: showPill ? 84 : 44, color: 'var(--m-fg)', borderColor: 'var(--m-line)' }}>
          <span>Save take · {take.frames.length} frames</span>
          <input data-testid="face-track-take-name" value={name} onChange={(e) => setName(e.target.value)}
            style={{ background: 'var(--m-bg)', color: 'var(--m-fg)', border: '1px solid var(--m-line)', borderRadius: 4, fontFamily: 'var(--m-font)', fontSize: 11, padding: '2px 6px', width: 180 }} />
          <button type="button" data-testid="face-track-save" disabled={saveState === 'saving'} onClick={save}
            style={{ ...btn(), color: 'var(--m-accent)', borderColor: 'var(--m-accent)' }}>Save</button>
          <button type="button" data-testid="face-track-discard" onClick={() => { setTake(null); setSaveState('clean') }} style={btn()}>Discard</button>
          <span data-testid="face-track-save-badge" data-state={saveState}>
            <DirtyBadge state={saveState} message={saveError ?? undefined} />
          </span>
        </div>
      )}
    </>
  )
}
